import { useSubscription } from '@apollo/client';
import { isEmpty, groupBy } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AuthContext from '../../auth/context/AuthContext';
import { GET_REFERRAL_NOTES_BY_UUID } from '../gql';
import ReferralNotesTable from '../tables/ReferralNotesTable';
import { Box, Grid, Tabs, Tab } from '@mui/material';
import { MyButton } from '../../common/components';
import { useCreateReferralNote } from '../redux/createReferralNote';
import Swal from 'sweetalert2';
import MyTextField from '../../common/components/MyTextField';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChatIcon from '@mui/icons-material/ChatOutlined';

export default function AllReferralNotes({ uuid, isExternal, isDataTab = false, DataComponent = null }) {
  const [clinicId, setClinicId] = useState('');
  const [activeTab, setActiveTab] = useState(isDataTab ? 'data' : 'internal');
  const currentUser = useContext(AuthContext);
  const [noteText, setNoteText] = useState('');

  const {
    createReferralNote,
    createReferralNotePending,
    createReferralNoteError,
    dismissCreateReferralNoteError,
  } = useCreateReferralNote();

  useEffect(() => {
    if (!isEmpty(currentUser)) {
      setClinicId(currentUser.clinic.id);
    }
  }, [currentUser]);

  const { loading, data } = useSubscription(GET_REFERRAL_NOTES_BY_UUID, {
    variables: { uuid, clinicId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });

  const groupedData = useMemo(() => {
    if (!data?.rows) return { internal: [], external: [], system: [] };
    return groupBy(data.rows, (note) => note.group?.name || 'unknown');
  }, [data]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setNoteText('');
  };

  const onCreateReferralNote = useCallback(
    (group) => {
      dismissCreateReferralNoteError();
      const formData = new FormData();
      formData.append('note', noteText);
      formData.append('group', group);
      createReferralNote(uuid, formData)
        .then(() => setNoteText(''))
        .catch(() =>
          Swal.fire({
            icon: 'error',
            text: createReferralNoteError,
            showConfirmButton: true,
          })
        );
    },
    [createReferralNote, createReferralNoteError, dismissCreateReferralNoteError, noteText, uuid]
  );

  const renderTable = (group) => (
    <Box>
      {group !== 'system' && (
        <Grid container spacing={2} alignItems="center" mb={2}>
          <Grid item md={10} xs={12}>
            <MyTextField
              label={`add a ${group === 'internal' ? 'note' : 'message'}`}
              variant="outlined"
              size="small"
              fullWidth
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !createReferralNotePending && noteText.trim()) {
                  e.preventDefault();
                  onCreateReferralNote(group);
                }
              }}
            />
          </Grid>
          <Grid item md={2} xs={12}>
            <MyButton
              style={{ height: '100%' }}
              type="button"
              color="primary"
              variant="outlined"
              loading={createReferralNotePending}
              disabled={!noteText.trim()}
              onClick={() => onCreateReferralNote(group)}
            >
              {group === 'internal' ? 'Add Note' : 'Message'}
            </MyButton>
          </Grid>
        </Grid>
      )}

      <ReferralNotesTable
        loading={loading}
        group={group}
        data={{
          rows: groupedData[group] || [],
          count: (groupedData[group] || []).length,
        }}
      />
    </Box>
  );

  return (
    <Box>
      {/* Tabs Header */}
      <Box
        sx={{
          backgroundColor: '#f1f1f1',
          borderRadius: '8px',
          padding: '4px',
          width: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{
            minHeight: '32px',
            border: 'none',
            p: 0,
            m: 0,
            '& .MuiTabs-flexContainer': {
              gap: 0,
              padding: 0,
              margin: '0px -5px 0px',
            },
            '& .MuiTab-root': {
              minHeight: '32px',
              textTransform: 'none',
              fontSize: '0.85rem',
              fontWeight: 500,
              borderRadius: '6px',
              transition: 'all 0.2s',
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
              flex: 1,
              maxWidth: 'none',
              padding: '0px',
            },
            '& .Mui-selected': {
              backgroundColor: '#fff',
              fontWeight: 600,
              boxShadow: 1,
              border: 'none',
              outline: 'none',
            },
          }}
        >
          {isDataTab && DataComponent && (
            <Tab
              label="Patient Info"
              value="data"
              icon={<PersonIcon fontSize="small" sx={{ mr: 0.5 }} />}
              iconPosition="start"
            />
          )}
          <Tab
            label="Notes"
            value="internal"
            icon={<DescriptionIcon fontSize="small" sx={{ mr: 0.5 }} />}
            iconPosition="start"
          />
          {isExternal && (
            <Tab
              label="External Messages"
              value="external"
              icon={<ChatIcon fontSize="small" sx={{ mr: 0.5 }} />}
              iconPosition="start"
            />
          )}
          <Tab
            label="Log"
            value="system"
            icon={<AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ mt: 2 }}>
        {activeTab === 'data' && isDataTab && DataComponent}
        {activeTab === 'internal' && renderTable('internal')}
        {activeTab === 'system' && renderTable('system')}
        {activeTab === 'external' && isExternal && renderTable('external')}
      </Box>
    </Box>
  );
}
